import { useFloating, offset, flip, shift, size, autoUpdate } from '@floating-ui/react';
import { useMemo } from 'react';
import { tokens } from '../styles/tokens';

interface UseCommentPositioningOptions {
  /** Whether the sidebar is currently open */
  isSidebarOpen: boolean;
  /** Offset distance from reference element (pixels) */
  offsetDistance?: number;
  /** Padding from viewport edges (pixels) */
  viewportPadding?: number;
  /** Maximum width of floating element (pixels) */
  maxWidth?: number;
  /** Maximum height of floating element (pixels) */
  maxHeight?: number;
}

/**
 * Custom hook for positioning comment UI elements using Floating UI.
 * Handles auto-placement, boundary detection, and collision avoidance.
 *
 * Features:
 * - Auto-placement: Chooses optimal side based on available space
 * - Boundary detection: Keeps elements within viewport
 * - Sidebar collision: Avoids sidebar when open
 * - Smooth updates: Automatically repositions on scroll/resize
 *
 * @param options - Positioning configuration options
 * @param options.isSidebarOpen - Whether sidebar is currently open
 * @param options.offsetDistance - Gap between reference and floating element (default: 12px)
 * @param options.viewportPadding - Minimum padding from viewport edges (default: 8px)
 * @param options.maxWidth - Maximum width of floating element (default: 480px)
 * @param options.maxHeight - Maximum height of floating element (default: 600px)
 *
 * @returns Positioning utilities
 * @returns refs - Reference setters for elements
 * @returns floatingStyles - CSS styles to apply to floating element
 * @returns isPositioned - Whether positioning is calculated
 * @returns update - Manual update function
 *
 * @example
 * ```tsx
 * const { refs, floatingStyles, isPositioned } = useCommentPositioning({
 *   isSidebarOpen,
 * });
 *
 * // Set reference element (e.g., pin)
 * refs.setReference(pinElement);
 *
 * // Apply styles to floating element (e.g., thread viewer)
 * <div ref={refs.setFloating} style={floatingStyles}>
 *   {content}
 * </div>
 * ```
 */
export function useCommentPositioning({
  isSidebarOpen,
  offsetDistance = 12,  // tokens.spacing.s
  viewportPadding = 16,  // Increased from 8px for better boundary safety
  maxWidth = 480,
  maxHeight = 600,
}: UseCommentPositioningOptions) {

  // Parse sidebar width from token (e.g., "360px" -> 360)
  const sidebarWidth = parseInt(tokens.layout.sidebarWidth, 10);

  const middleware = useMemo(() => [
    offset(offsetDistance),
    flip({
      fallbackPlacements: ['bottom', 'top', 'right', 'left'],
      // Use asymmetric padding for flip boundary when sidebar is open
      padding: isSidebarOpen
        ? { top: viewportPadding, bottom: viewportPadding, left: viewportPadding, right: sidebarWidth + viewportPadding }
        : viewportPadding,
    }),
    shift({
      // Use asymmetric padding to account for sidebar when open
      // This prevents the floating element from overlapping the sidebar
      padding: isSidebarOpen
        ? { top: viewportPadding, bottom: viewportPadding, left: viewportPadding, right: sidebarWidth + viewportPadding }
        : viewportPadding,
    }),
    size({
      apply({ availableWidth, availableHeight, elements }) {
        // Reduce available width by sidebar width when open
        const effectiveMaxWidth = isSidebarOpen
          ? Math.min(maxWidth, availableWidth - sidebarWidth)
          : Math.min(maxWidth, availableWidth);
        Object.assign(elements.floating.style, {
          maxWidth: `${effectiveMaxWidth}px`,
          maxHeight: `${Math.min(maxHeight, availableHeight)}px`,
        });
      },
      padding: isSidebarOpen
        ? { top: viewportPadding, bottom: viewportPadding, left: viewportPadding, right: sidebarWidth + viewportPadding }
        : viewportPadding,
    }),
  ], [isSidebarOpen, offsetDistance, viewportPadding, maxWidth, maxHeight, sidebarWidth]);

  const { refs, floatingStyles, isPositioned, update } = useFloating({
    placement: 'bottom-start',
    middleware,
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',  // Use fixed positioning for more robust overlay behavior
  });

  return {
    refs,
    floatingStyles,
    isPositioned,
    update,
  };
}

/**
 * Creates a virtual element for cursor-based positioning.
 * Used when positioning relative to a click event instead of a DOM element.
 *
 * @param x - X coordinate in viewport
 * @param y - Y coordinate in viewport
 * @returns Virtual element with getBoundingClientRect method
 *
 * @example
 * ```tsx
 * const handleClick = (e: MouseEvent) => {
 *   const virtualElement = createVirtualElement(e.clientX, e.clientY);
 *   refs.setReference(virtualElement);
 * };
 * ```
 */
export function createVirtualElement(x: number, y: number) {
  return {
    getBoundingClientRect: () => ({
      width: 0,
      height: 0,
      x,
      y,
      top: y,
      left: x,
      right: x,
      bottom: y,
    }),
  };
}
